'use client';
import { useCategoryTree } from '@/hooks/useCategoryTree';

import React, { useCallback } from 'react';
import { TelegramCategoryDynamicParameterResponseDto, TelegramCategoryParameterResponseDto } from '@/generated';
import { CategorySelectElem } from './FiltersCategorySelect';
import { useTranslations } from 'next-intl';
import { FiltersMinMax, MinMaxValueType } from './FiltersMinMax';
import { ParamItem } from '@/utils/SEOPathModule/convertPathToParams';
import { FiltersSelect } from './FiltersSelect';
import { CategoryFallbackSelected, FallbackCategorySelect } from './FiltersCategorySelect/FallbackCategorySelect';
import { DictsGateState } from './types';
import { FreePriceToggle } from './FreePriceToggle';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

type Props = {
  onSubmit: () => void;
  onCancel: () => void;
  shownParams: ShownParams;
  categories: string[];
  onUpdateCategory: (newCat: string[]) => void;
  onSubmitWithCategory: (newCat: string[]) => void;
  priceValue: MinMaxValueType;
  onChangePrice: (nv: MinMaxValueType) => void;
  isFree: boolean;
  onChangeIsFree: (nv: boolean) => void;
  params: ParamItem[];
  onUpdateParam: (id: string, val: string | string[] | { from?: number; to?: number }) => void;
  categoryInitialData: CategoryFallbackSelected;
  dictsGateState?: DictsGateState;
  showFree?: boolean;
};

type ShownParams = (TelegramCategoryParameterResponseDto | TelegramCategoryDynamicParameterResponseDto)[];

type NumberParamValue = Extract<ParamItem['value'], { from?: number; to?: number }>;

const isNumberParamValue = (value: ParamItem['value'] | undefined): value is NumberParamValue => {
  return value !== undefined && !Array.isArray(value) && typeof value === 'object';
};

export const SearchFiltersList: React.FC<Props> = ({
  onSubmit,
  onCancel,
  onUpdateParam,
  shownParams,
  params,
  priceValue,
  onChangePrice,
  isFree,
  onChangeIsFree,
  categories,
  onSubmitWithCategory,
  categoryInitialData,
  dictsGateState = 'idle',
  showFree = false,
}) => {
  const t = useTranslations('filters');

  const { data: categoriesData = [], isLoading: isCategoriesLoading } = useCategoryTree();
  const isBlocked = dictsGateState !== 'idle';

  const createOnUpdate = useCallback(
    (paramId: string) => {
      return (newVal: string | string[]) => {
        onUpdateParam(paramId, newVal);
      };
    },
    [onUpdateParam],
  );

  return (
    <div className='flex w-full flex-col gap-[24px] relative'>
      {isCategoriesLoading ? (
        <FallbackCategorySelect
          shownCategories={categoryInitialData.shownCategories}
          selectedCategory={categoryInitialData.selectedCategory}
        />
      ) : (
        <CategorySelectElem data={categoriesData} onChange={onSubmitWithCategory} value={categories} />
      )}

      {shownParams.map((param) => {
        const foundParam = params.find((el) => el.id === param.id);
        const paramValue = foundParam?.value;
        const onUpdate = createOnUpdate(param.id);
        const selectParams = param.enumOptions?.map((val: string) => ({ label: val, value: val })) || [];
        const isNumberParam = param.type === 'NUMBER';
        const rangeValue = isNumberParamValue(paramValue) ? paramValue : undefined;
        const stringValue = typeof paramValue === 'string' ? paramValue : '';
        const multiValue = Array.isArray(paramValue) ? paramValue : stringValue ? [stringValue] : [];

        if (isNumberParam) {
          return (
            <div key={param.id} className='flex flex-col gap-[18px]'>
              <div className='text-m font-semibold! text-text-primary-day'>{param.name}</div>
              <FiltersMinMax
                value={{ min: rangeValue?.from, max: rangeValue?.to }}
                onChange={(val) => {
                  onUpdateParam(param.id, { from: val.min, to: val.max });
                }}
              />
            </div>
          );
        }

        return (
          <div key={param.id} className='flex flex-col gap-[18px]'>
            <div className='text-m font-semibold! text-text-primary-day'>{param.name}</div>
            {param.isMultiselectable ? (
              <FiltersSelect
                label={param.name || ''}
                onChange={onUpdate}
                multiselectMode={true}
                data={selectParams}
                value={multiValue}
              />
            ) : (
              <FiltersSelect
                label={param.name || ''}
                multiselectMode={false}
                onChange={onUpdate}
                data={selectParams}
                value={stringValue}
              />
            )}
          </div>
        );
      })}

      {showFree && <FreePriceToggle checked={isFree} onChange={onChangeIsFree} label={t('free_products')} />}

      {!isFree && (
        <div className='flex flex-col gap-[18px]'>
          <div className='text-m font-semibold! text-text-primary-day'>{t('price')}</div>
          <FiltersMinMax value={priceValue} onChange={onChangePrice} />
        </div>
      )}

      <div className='flex gap-[4px] sticky bottom-[16px]'>
        <Button variant='outline' onClick={onCancel} className='w-1/2 h-auto py-[10px]'>
          {t('clear')}
        </Button>

        <Button onClick={onSubmit} className='w-1/2 h-auto py-[10px]'>
          {t('submit')}
        </Button>
      </div>

      {isBlocked && (
        <div className='absolute inset-0 z-10 rounded-[12px] bg-white/75 backdrop-blur-[1px] flex items-center justify-center'>
          {dictsGateState === 'loading' && (
            <div className='flex flex-col items-center gap-[8px] text-text-primary-day'>
              <Spinner className='size-[28px] text-primary-green-day' />
            </div>
          )}
          {dictsGateState === 'error' && (
            <div className='text-[14px] px-[16px] text-center text-text-primary-day'>Failed to load filters data.</div>
          )}
        </div>
      )}
    </div>
  );
};
